import type { Platform } from './types';

const NO_VALUE = '—';

/**
 * Normalize a raw platform amount to a display-currency number (major units).
 *
 * The two platforms deliver money in different denominations:
 *  - Google report rows already arrive in display units — the backend divides
 *    cost_micros by 1e6 in report_mixins/base.py before it reaches the UI.
 *  - Meta dashboard rows arrive in cents (MetaInsightsSnapshot.spend_cents).
 *
 * This is the single seam that erases that difference for the consolidated
 * surface, so no table or tile re-implements the conversion (#5972).
 */
export function toDisplayAmount(raw: number, platform: Platform): number {
  return platform === 'meta' ? raw / 100 : raw;
}

/**
 * Format a display-currency amount with a currency symbol. Unifies fmt()
 * (cells.tsx, Google) and formatCents() (meta-metrics.tsx, Meta): grouped
 * thousands, up to 2 decimals, "—" for nullish / non-finite.
 */
export function formatMoney(amount: number | null | undefined, symbol: string): string {
  if (amount == null || !Number.isFinite(amount)) return NO_VALUE;
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** Normalize a raw platform amount and format it in one step. */
export function formatPlatformMoney(
  raw: number | null | undefined,
  platform: Platform,
  symbol: string,
): string {
  if (raw == null || !Number.isFinite(raw)) return NO_VALUE;
  return formatMoney(toDisplayAmount(raw, platform), symbol);
}
